import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import {
    Container,
    Box,
    Typography,
    Paper,
    Button,
} from '@mui/material';
import logo from '../assets/LOGO.png';
import { adminLogin } from '../services/adminServices/AdminServices';


const paperStyles = {
    width: '100%',
    height: '45%',
    minHeight: '230px',
    padding: 4,
    borderRadius: 3,
    boxShadow: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    backgroundColor: '#fff',
};

const AdminLogin = () => {
    const navigate = useNavigate();
    const { coeName } = useParams();

    const coeDisplayName = React.useMemo(() => {
        if (!coeName || ['admin', 'mentor', 'college-viewer'].includes(coeName)) {
            return null;
        }
        return coeName.replace(/-/g, ' ');
    }, [coeName]);

 const handleGoogleLoginSuccess = async (tokenResponse) => {
    try {
        const decoded = jwtDecode(tokenResponse.credential);
        const response = await adminLogin(decoded.email, tokenResponse.credential);

        const userData = response?.data?.data;

        if (userData) {
            // For report viewer, enforce COE matching if accessed via a specific COE URL
            if (userData.role === 'coe_report_viewer') {
                const userCoe = (userData.coeName || userData.coe_name || '').trim();
                const formattedCoeName = userCoe ? userCoe.replace(/\s+/g, '-') : 'coe';

                if (coeName && !['admin', 'mentor', 'college-viewer'].includes(coeName)) {
                    const normalize = (str) => (str ? str.trim().toLowerCase().replace(/[\s-_]+/g, '') : '');
                    if (normalize(userCoe) !== normalize(coeName)) {
                        toast.error(`Unauthorized access. You are not authorized to view reports for ${coeName.replace(/-/g, ' ')}.`);
                        return;
                    }
                }

                localStorage.setItem("user", JSON.stringify(userData));
                localStorage.setItem("token", userData.token);
                toast.success('Login Successfully');

                const redirectPath = `/${formattedCoeName}/learner-reviews`;
                setTimeout(() => navigate(redirectPath), 1000);
                return;
            }

            // For other roles (super_admin, coe_admin, coe_report_admin)
            localStorage.setItem("user", JSON.stringify(userData));
            localStorage.setItem("token", userData.token);
            toast.success(response.data.message || 'Login Successfully');

            // Role-based redirect
            let redirectPath = '/admin/coe-center'; // default fallback

            if (userData.role === 'super_admin') {
                redirectPath = '/admin/add-admin';
            } else if (userData.role === 'coe_admin') {
                redirectPath = '/admin/coe-center';
            } else if (userData.role === 'coe_report_admin') {
                redirectPath = '/admin/daily-report';
            }

            setTimeout(() => navigate(redirectPath), 1000);
        } else {
            throw new Error('Invalid response from server.');
        }
    } catch (error) {
        toast.error(error.response?.data?.message || 'Something went wrong. Please try again.');
    }
};


    const handleGoogleLoginFailure = () => {
        toast.error('Google login failed. Please try again.');
    };

    return (
        <Container
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: { xs: "auto", sm: '38vw' },
                padding: 2,
            }}
        >
            <Paper sx={paperStyles}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ display:"flex",justifyContent:"center", mb: 1 }}>
                        <img src={logo} alt="Logo" />
                    </Typography>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 'bold', color: '#3f51b5', letterSpacing: 1.5, textAlign: 'center' }}
                    >
                        Welcome Back!
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    sx={{
                        // width: '50%',
                        height: '60px',
                        fontSize: '16px',
                        fontWeight: '500',
                        textTransform: 'none',
                        borderRadius: '5px',
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: '#1976d2',
                        '&:focus': {
                            boxShadow: '0 0 3px 1px rgba(66, 133, 244, 0.5)',
                        },
                    }}
                >
                    <GoogleLogin
                        onSuccess={handleGoogleLoginSuccess}
                        onError={handleGoogleLoginFailure}
                        useOneTap
                        text="signin_with"
                    />
                </Button>
            </Paper>
        </Container>
    );
};

export default AdminLogin;
